<template>
  <Dialog
    :visible="!!template"
    modal
    :header="`Assign Template: ${template?.name ?? ''}`"
    class="w-full max-w-md"
    :draggable="false"
    @update:visible="onVisibleChange"
  >
    <div class="space-y-3 pt-2">
      <p class="text-sm text-gray-600">
        Select a programme to use this transcript template as its default.
      </p>

      <div class="flex flex-col gap-0.5">
        <label class="text-xs font-medium text-gray-600">Programme *</label>
        <ProgrammeSelect
          v-model="selectedProgrammeId"
          placeholder="Select a programme"
          data-testid="assign-template-programme-select"
        />
      </div>
    </div>

    <template #footer>
      <Button
        size="small"
        label="Cancel"
        severity="secondary"
        text
        data-testid="assign-template-cancel"
        @click="close"
      />
      <Button
        size="small"
        label="Assign"
        icon="pi pi-check"
        :loading="assigning"
        :disabled="!selectedProgrammeId"
        data-testid="assign-template-confirm"
        @click="submit"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import ProgrammeSelect from '@/components/common/ProgrammeSelect.vue';
import { SET_PROGRAMME_TRANSCRIPT_TEMPLATE_DEFAULT } from '@/graphql/transcript/mutations';

const props = defineProps<{
  template: { id: string; name: string } | null;
}>();

const emit = defineEmits<{
  close: [];
  assigned: [];
}>();

const toast = useToast();
const selectedProgrammeId = ref<string | undefined>(undefined);

const { mutate, loading: assigning } = useMutation(SET_PROGRAMME_TRANSCRIPT_TEMPLATE_DEFAULT);

watch(() => props.template, (val) => {
  if (val) {
    selectedProgrammeId.value = undefined;
  }
});

const close = () => emit('close');

const onVisibleChange = (visible: boolean) => {
  if (!visible) close();
};

const submit = async() => {
  if (!props.template?.id || !selectedProgrammeId.value) return;
  try {
    await mutate({
      programme_id: selectedProgrammeId.value,
      transcript_template_id: props.template.id,
    });
    toast.add({
      severity: 'success',
      summary: 'Assigned',
      detail: `Template "${props.template.name}" assigned as default.`,
      life: 3000,
    });
    emit('assigned');
    close();
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Failed to assign template';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
  }
};
</script>
